import Button from "components/BaseWidgets/button";
import AddOnCard from "./components/AddOnCard";
import PlanDetailsCard from "./components/PlanDetailsCard"
import "./style.scss";
import { usePremium } from "context/PremiumContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useAddOns,
  useCreatePlan,
  useMyPlans,
  usePaymentCallback,
  usePaymentStatus,
} from "hooks/api/usePlans";
import Skeleton from "components/BaseWidgets/skeleton";
import { getFreePlanData, getPremiumPlanData } from "./utils";
import FailureModal from "./components/FailureModal";
import { useEffect, useState } from "react";
import AlertNotification from "components/CustomWidgets/alertNotification";
import { useQueryClient } from "@tanstack/react-query";

function Plans() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { isPremium } = usePremium();
  const navigate = useNavigate();
  const baseurl = import.meta.env.VITE_REDIRECT;
  const txnid = searchParams.get("txnid");
  const { data: subMyPlans = {}, isLoading: isPlanLoading } = useMyPlans();
  const { data: addOnsData = {}, isLoading: isAddOnLoading } = useAddOns({params: { see_all: true }});
  const addOns = addOnsData?.data?.add_on_data ?? [];
  const premiumPlanData = getPremiumPlanData(subMyPlans?.data?.plan_data[0] || []);
  const freePlanData = getFreePlanData(subMyPlans?.data?.plan_data[0] || []);

  const [isFailed, setFailed] = useState(false);

  const { mutate: createPlan, isLoading } = useCreatePlan({
    onSuccess: (data) => {
      const paymentDetails = data?.payment_data;

      if (!paymentDetails?.url || !paymentDetails?.payload) {
        console.error("Payment details missing!");
        return;
      }

      const { url, payload } = paymentDetails;

      const successUrl = `${baseurl}plans?txnid=${payload?.txnid}`;
      const failureUrl = `${baseurl}plans?txnid=${payload?.txnid}`;

      const form = document.createElement("form");
      form.method = "POST";
      form.action = url;

      const orderedFields = {
        key: payload.key,
        txnid: payload.txnid,
        amount: parseFloat(payload.amount).toFixed(2),
        productinfo: payload.productinfo,
        firstname: payload.firstname,
        email: payload.email,
        phone: payload.phone,
        lastname: payload.lastname || "",
        address1: payload.address1 || "",
        surl: successUrl,
        furl: failureUrl,
        hash: payload.hash,
        service_provider: payload.service_provider || "payu_paisa",
        udf1: "",
        udf2: "",
        udf3: "",
        udf4: "",
        udf5: "",
      };
      Object.entries(orderedFields).forEach(([k, v]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = k;
        input.value = v ?? "";
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    },
  });
  const { isLoading: loading } = usePaymentCallback(txnid, {
    enabled: Boolean(txnid),
  });

  const { mutate: fetchPaymentStatus } = usePaymentStatus({
    onSuccess: (data) => {
      if (!txnid) return;
      const transactionData = data?.transaction_details[txnid];
      queryClient.invalidateQueries({ queryKey: ["my_plans"] });
      if (transactionData?.status === "success") {
        AlertNotification("success", "Plan purchased");
        navigate("/plans", { replace: true });
      } else if (transactionData?.status === "failed") {
        setFailed(true);
        AlertNotification("error", transactionData?.error_Message);
      } else {
        AlertNotification("error", transactionData?.error_Message);
      }
    },
  });

  useEffect(() => {
    if (txnid) {
      fetchPaymentStatus({ txnid });
    }
  }, [txnid, fetchPaymentStatus]);

  return (
    <>
      <div className="plan-container">
        <div className="plan-section flex flex-col gap-8 current-plan-container">
          <div className="flex flex-col gap-1">
            <h1 className="main-title">My plan</h1>
            <p className="main-description">
              View and manage your current subscription plan with ease.
            </p>
          </div>
          {isPlanLoading ? (
            <Skeleton label={false} count={1} height={200} width="100%" />
          ) : isPremium && premiumPlanData ? (
            <PlanDetailsCard
              btnText="Renew subscription"
              data={premiumPlanData}
              isLoading={isLoading}
              onClick={() => {
                if (premiumPlanData.plan_id) {
                  createPlan({
                    plan_id: premiumPlanData?.plan_id,
                  });
                  return;
                }
                console.error("plan id not found");
              }}
            />
          ) : (
            // TODO: Static data , api not provided for free plan
            <PlanDetailsCard
              isFree
              data={freePlanData}
              onClick={() => navigate("/plans/list")}
            />
          )}
        </div>
        {isPremium && (
          <div className="add-on-container">
            <div className="plan-section flex flex-col gap-8">
              <div className="flex flex-col gap-1">
                <span className="sub-title">Add ons for your subscription</span>
                <p className="sub-description">
                  Find your perfect match with Personalized Assist, a premium
                  service designed for those who seek expert guidance in their
                  matchmaking journey. Let our dedicated relationship experts
                  assist you in finding the most compatible partner based on
                  your preferences.
                </p>
              </div>
              {isAddOnLoading ? (
                <Skeleton label={false} count={3} height={150} width="100%" />
              ) : (
                <div className="flex flex-wrap gap-8 addOn-card-container">
                  {addOns.length > 0 ? (
                    addOns.map((item, i) => (
                      <AddOnCard
                        key={item.id}
                        isLoading={isLoading}
                        onClick={() => {
                          createPlan({
                            addon_id: item?.id,
                          });
                        }}
                        data={{
                          title: item?.name,
                          validity: item?.duration,
                          price: item?.amount,
                          views: item?.contact_view_limit,
                          badge: item?.badge,
                        }}
                      />
                    ))
                  ) : (
                    <p className="no-addons">No add-ons available currently</p>
                  )}
                </div>
              )}
              <Button href="/plans/list" className="explore-btn" height={44}>
                Explore more plans
              </Button>
            </div>
          </div>
        )}
      </div>
      <FailureModal open={isFailed} setOpen={setFailed} />
    </>
  );
}

export default Plans;
